// useCustomTemplates — Persiste templates de skills criados pelo user no
// localStorage. Sem backend pra v1 — simples, instantâneo, sobrevive a
// reloads. Migração pra D1 fica pra quando user pedir sync entre devices.
//
// Schema:
//   {
//     id: string,         // uuid local
//     emoji: string,
//     title: string,
//     description: string,
//     slashes: string[],  // ['/foo', '/bar']
//     body: string,       // texto adicional opcional
//     tags: string[],
//     custom: true,
//     createdAt: number,
//     updatedAt: number,
//   }

import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'apolo:custom-templates';

function loadAll() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

function persistAll(templates) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
  } catch { /* quota / private mode */ }
}

function genId() {
  return `tpl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function useCustomTemplates() {
  const [templates, setTemplates] = useState(() => loadAll());

  // Sincroniza entre tabs / janelas Electron mesmo dispositivo
  useEffect(() => {
    function onStorage(e) {
      if (e.key === STORAGE_KEY) setTemplates(loadAll());
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const save = useCallback((tpl) => {
    const now = Date.now();
    setTemplates((prev) => {
      let next;
      if (tpl.id && prev.find((t) => t.id === tpl.id)) {
        next = prev.map((t) => t.id === tpl.id ? { ...t, ...tpl, updatedAt: now } : t);
      } else {
        next = [...prev, {
          ...tpl,
          id: tpl.id || genId(),
          custom: true,
          createdAt: now,
          updatedAt: now,
        }];
      }
      persistAll(next);
      return next;
    });
  }, []);

  const remove = useCallback((id) => {
    setTemplates((prev) => {
      const next = prev.filter((t) => t.id !== id);
      persistAll(next);
      return next;
    });
  }, []);

  const duplicate = useCallback((id) => {
    setTemplates((prev) => {
      const orig = prev.find((t) => t.id === id);
      if (!orig) return prev;
      const now = Date.now();
      const copy = {
        ...orig,
        id: genId(),
        title: `${orig.title} (cópia)`,
        createdAt: now,
        updatedAt: now,
      };
      const next = [...prev, copy];
      persistAll(next);
      return next;
    });
  }, []);

  return { templates, save, remove, duplicate };
}
